import { default as React } from 'react';
import { Box, TextField, Typography } from '@material-ui/core';
import { Table } from '@backstage/core-components';
import Autocomplete from '@mui/material/Autocomplete';

import { DisasterRecovery } from '../types';

const AWS_REGIONS = [
  'us-east-1',
  'us-west-2',
  'us-east-2',
  'ap-east-1',
  'ap-southeast-1',
  'ap-southeast-2',
  'eu-west-1',
  'eu-west-2',
];

interface DisasterRecoveryEditDialogProps {
  disasterRecovery?: DisasterRecovery;
  setDisasterRecovery: React.Dispatch<React.SetStateAction<DisasterRecovery>>;
}

export const DisasterRecoveryEditDialog = ({
  disasterRecovery: disasterRecoveryFeature,
  setDisasterRecovery,
}: DisasterRecoveryEditDialogProps) => {
  const [primaryRegion, setPrimaryRegion] = React.useState(
    disasterRecoveryFeature?.primary_region || '',
  );
  const [secondaryRegion, setSecondaryRegion] = React.useState(
    disasterRecoveryFeature?.secondary_region || '',
  );
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );

  const validateRegions = (primary: string, secondary: string) => {
    if (!primary || !secondary) {
      setValidationError('Both primary and secondary regions are required');
      return false;
    }
    if (primary === secondary) {
      setValidationError('Primary and secondary regions cannot be the same');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const getFieldError = (regionType: string) => {
    if (!validationError) return '';

    if (validationError.includes('required')) {
      if (regionType === 'Primary Region' && !primaryRegion)
        return validationError;
      if (regionType === 'Secondary Region' && !secondaryRegion)
        return validationError;
    }

    if (validationError.includes('cannot be the same')) {
      return validationError;
    }

    return '';
  };

  const handlePrimaryRegionChange = (_event: any, newValue: string | null) => {
    const value = newValue || '';
    setPrimaryRegion(value);
    validateRegions(value, secondaryRegion);
    setDisasterRecovery(prev => ({
      ...prev,
      primary_region: value,
    }));
  };

  const handleSecondaryRegionChange = (
    _event: any,
    newValue: string | null,
  ) => {
    const value = newValue || '';
    setSecondaryRegion(value);
    validateRegions(primaryRegion, value);
    setDisasterRecovery(prev => ({
      ...prev,
      secondary_region: value,
    }));
  };

  const rows = [
    {
      region_type: 'Primary Region',
      region: primaryRegion,
      onChange: handlePrimaryRegionChange,
    },
    {
      region_type: 'Secondary Region',
      region: secondaryRegion,
      onChange: handleSecondaryRegionChange,
    },
  ];

  const columns = [
    {
      title: 'Region Type',
      width: '35%',
      render: (rowData: any) => {
        return (
          <Typography variant="subtitle1" style={{ whiteSpace: 'nowrap' }}>
            {rowData.region_type}
          </Typography>
        );
      },
    },
    {
      title: 'Region',
      width: '65%',
      render: (rowData: any) => {
        const fieldError = getFieldError(rowData.region_type);
        return (
          <Box>
            <Autocomplete
              value={rowData.region}
              onChange={rowData.onChange}
              options={AWS_REGIONS}
              disableClearable
              size="small"
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  fullWidth
                  required
                  error={!!fieldError}
                  placeholder={`Select ${rowData.region_type.toLowerCase()}`}
                />
              )}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Box style={{ minWidth: '500px', margin: '0 auto' }}>
      {validationError && (
        <Typography color="error" style={{ marginBottom: '16px' }}>
          {validationError}
        </Typography>
      )}
      <Table
        columns={columns}
        data={rows}
        options={{
          showTitle: false,
          loadingType: 'linear',
          header: false,
          search: false,
          padding: 'dense',
          toolbar: false,
          paging: false,
        }}
      />
    </Box>
  );
};
